import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  selectPartnerAccess,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { selectSectionSettings } from 'legacy/lut/features/partner360/sectionSettings'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { getExportPlanActualsValueType } from 'partner360/helpers/sectionSettings/getExportPlanActualsValueType'
import { getExportPlanActualsValueTypeKey } from 'partner360/helpers/sectionSettings/getExportPlanActualsValueTypeKey'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { useSavePartnerPlanExportMetric } from './useSavePartnerPlanExportMetric'

interface IOptions {
  actualsValueTypes: ActualsValueTypes[]
  actualsValueTypeDefault: ActualsValueTypes
  partnerId?: string
  fYear?: number
}

export const usePartnerPlanExport = ({
  actualsValueTypes,
  actualsValueTypeDefault,
  partnerId,
  fYear,
}: IOptions) => {
  const [actualsValueType, setActualsValueType] = useState<ActualsValueTypes>(ActualsValueTypes.Net)
  const [actualsValueTypeUser, setActualsValueTypeUser] = useState('')
  const [actualsValueTypeDate, setActualsValueTypeDate] = useState('')
  const [actualsValueTypeForConfirm, setActualsValueTypeForConfirm] = useState<ActualsValueTypes>(
    ActualsValueTypes.None
  )
  const { isDistributor } = useSelector(selectPartnerAccess)
  const programType = useSelector(selectProgramType)
  const sectionSettings = useSelector(selectSectionSettings)
  const isRetailer = programType === ProgramTypes.Retail

  const isHideTooltip = useMemo(
    () => actualsValueType === actualsValueTypeDefault,
    [actualsValueType, actualsValueTypeDefault]
  )

  const saveMetric = useSavePartnerPlanExportMetric(programType, partnerId, fYear)
  const changeNetNdp = useCallback(
    async (metric: ActualsValueTypes) => {
      await saveMetric({ metric })
      setActualsValueType(metric)
    },
    [saveMetric]
  )

  const confirmActualsValueTypeHandler = useCallback(async () => {
    await changeNetNdp(actualsValueTypeForConfirm)
  }, [changeNetNdp, actualsValueTypeForConfirm])

  const closeConfirmActualsValueTypeDialogHandler = useCallback(() => {
    setActualsValueTypeForConfirm(ActualsValueTypes.None)
  }, [])

  const netNdpChangeHandler = useCallback(
    async (newType: ActualsValueTypes) => {
      if (actualsValueType === actualsValueTypeDefault && newType !== actualsValueTypeDefault) {
        setActualsValueTypeForConfirm(newType)
      } else {
        await changeNetNdp(newType)
      }
    },
    [actualsValueType, actualsValueTypeDefault, changeNetNdp]
  )

  useEffect(() => {
    const exportPlanActualsValueType = getExportPlanActualsValueType(
      sectionSettings,
      programType,
      isRetailer,
      isDistributor,
      actualsValueTypes,
      actualsValueTypeDefault
    )
    setActualsValueType(exportPlanActualsValueType)

    const tag = getExportPlanActualsValueTypeKey(programType, isDistributor)
    setActualsValueTypeUser((sectionSettings as unknown as Record<string, string>)[`${tag}-user`])
    setActualsValueTypeDate((sectionSettings as unknown as Record<string, string>)[`${tag}-date`])
  }, [
    isDistributor,
    isRetailer,
    actualsValueTypeDefault,
    actualsValueTypes,
    programType,
    sectionSettings,
  ])

  return {
    actualsValueTypes,
    actualsValueType,
    actualsValueTypeUser,
    actualsValueTypeDate,
    actualsValueTypeForConfirm,
    isHideTooltip,
    netNdpChangeHandler,
    confirmActualsValueTypeHandler,
    closeConfirmActualsValueTypeDialogHandler,
  }
}
